<template>
    <div>
        <e-modal v-model="isStatusModalOpen" :can-cancel="false" full-screen :pt="{ pcMaximizeButton: { root: { style: 'display: none' } } }">
            <quiz-status :status="quizAttempt?.status" :color="color" :activity-id="activityId" />
        </e-modal>

        <e-modal v-model="isRedirectPromptOpen" :can-cancel="false" style="z-index: 10000">
            <quiz-attempt-redirect-modal
                :quiz-attempt-id="quizAttempt?.user?.quiz_active_id || -1"
                :status="quizAttempt?.status"
                @close="isRedirectPromptOpen = false"
            />
        </e-modal>

        <e-modal v-model="isClosingModalOpen" :can-cancel="false" style="z-index: 10001">
            <quiz-attempt-redirect-modal
                :quiz-attempt-id="quizAttempt?.user?.quiz_active_id || -1"
                closing
                @close="closeQuizAttempt"
            />
        </e-modal>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import QuizStatus from '@/components/quiz-attempt/quiz-status/QuizStatus.vue';
import QuizAttemptRedirectModal from '@/components/quizzes/QuizAttemptRedirectModal.vue';
import { useSocketConnection } from '@/composables/useSocketConnection.js';

const props = {
    onlineResponse: {
        type: Object,
        default: () => ({}),
    },
};

const $store = useStore();
const $route = useRoute();
const $router = useRouter();

const isRedirectPromptOpen = ref(false);
const isStatusModalOpen = ref(false);
const isClosingModalOpen = ref(false);
const quizAttempt = ref(null);
const attemptCount = ref(0);
const color = computed(() => $store.getters['User/preferredTheme']());
const { user } = $store.state.Auth;

const activityId = computed(() => $route.params.activityId);

// Listen for forced quiz status
const channel = `quiz-attempt-status-${user.id}`;

const openStatusModal = attempt => {
    if (isStatusModalOpen.value) {
        isStatusModalOpen.value = false;
    }

    quizAttempt.value = attempt;
    isStatusModalOpen.value = true;
};

const openRedirectPrompt = attempt => {
    quizAttempt.value = attempt;

    isRedirectPromptOpen.value = true;
};

const closeQuizAttempt = async () => {
    isClosingModalOpen.value = false;

    // Compute safe redirect targets from route first, then user store fallback
    const userActivity = $store.getters['User/get']('quiz_activity');
    const fallbackActivityId = userActivity?.activity?.id ?? userActivity?.id ?? null;
    const fallbackCourseId = userActivity?.activity?.course?.id ?? userActivity?.course?.id ?? null;

    const resolvedActivityId = $route.params.activityId ?? fallbackActivityId;
    let courseId = $route.params.courseId ?? fallbackCourseId;

    // If we have activityId but not courseId, try to resolve from Activities store or fetch
    if (resolvedActivityId && !courseId) {
        const activityFromStore = $store.getters['Activities/getByKey']({
            keyName: 'id',
            key: Number(resolvedActivityId),
        });

        courseId =
            activityFromStore?.course?.id ??
            activityFromStore?.activity?.course?.id ??
            activityFromStore?.course_id ??
            null;

        if (!courseId) {
            try {
                const resp = await $store.dispatch('Activities/fetchOne', resolvedActivityId);
                const data = resp?.data?.data;
                courseId = data?.course_id ?? data?.course?.id ?? null;
            } catch (_e) {
                // ignore and fall back to best-effort redirect below
            }
        }
    }

    // If we have both IDs, go back to activity view; otherwise, go home
    const target =
        resolvedActivityId && courseId
            ? { name: 'activity-view', params: { activityId: resolvedActivityId, courseId } }
            : { name: 'home' };

    const { href } = $router.resolve(target);
    window.location.href = href;
};

// If online check returns with quiz_active_id and is not in an attempt
// reload so middleware can redirect to quiz attempt

// Do not check on first load
watch(
    () => props.onlineResponse,
    async response => {
        if (attemptCount.value >= 1) {
            const quizAttemptRoute = $route.name ? $route.name.includes('attempt') : false;
            const { meta } = response;

            if (meta.quiz_active_id && !quizAttemptRoute && !isStatusModalOpen.value) {
                await openRedirectPrompt({
                    status: null,
                    quizAttemptId: meta.quiz_active_id,
                });
            }
        }

        attemptCount.value += 1;
    },
    { deep: true, immediate: true }
);

if (user) {
    useSocketConnection({
        channel,
        event: 'QuizAttemptForceStatus',
        callback: async event => {
            if (['in-waiting-room', 'in-waiting-room-post'].some(check => check === event.status)) {
                $store.commit('Auth/UPDATE_QUIZ_ACTIVE_ID', event.user.quiz_active_id);
                $store.commit('User/SET', { field: 'quiz_active_id', value: event.user.quiz_active_id });

                openStatusModal(event);
            }

            if (event.status === 'attempt-started') {
                await openRedirectPrompt(event);
            }

            if (event.status && event.status.includes('review')) {
                const quizId = $route.params.id;
                quizAttempt.value = event;
                isStatusModalOpen.value = false;

                await $store.dispatch(`QuizAttempt/${quizId}/endAndApplyStatus`, {
                    status: event.status,
                });
            }

            if (!event.status) {
                // Instructor ended quiz — close status modal and show closing redirect
                isStatusModalOpen.value = false;
                quizAttempt.value = event;
                isClosingModalOpen.value = true;
            }
        },
    });
}
</script>
